<template>
  <div
    ref="container"
    class="baseLottie"
    :style="{ width, height }"
    :class="class"
  ></div>
</template>

<script>
export default {
  name: "BaseLottie",
  props: {
    path: { type: String, default: "" },
    animationData: { type: Object, default: null },
    loop: { type: [Boolean, Number], default: false },
    autoplay: { type: Boolean, default: true },
    width: { type: String, default: "100%" },
    height: { type: String, default: "100%" },
    renderer: { type: String, default: "svg" },
    lazy: { type: Boolean, default: false },
    delay: { type: Number, default: 0 }, // optional delay in ms
    class: { type: String, default: "" },
    name: { type: String, default: "" },
    callBackFun: { type: Boolean, default: false },
  },
  data() {
    return {
      animation: null,
      observer: null,
      lottie: null,
      isVisible: false,
    };
  },

  mounted() {
    // SSR safety
    if (typeof window === "undefined") return;

    if (this.lazy) {
      this.initObserver();
    } else {
      this.loadLottie();
    }
  },

  beforeUnmount() {
    this.destroyAnimation();
    this.observer?.disconnect();
  },

  watch: {
    path: "reloadAnimation",
    animationData: {
      handler: "reloadAnimation",
    },
  },

  methods: {
    async loadLottie() {
      if (!this.$refs.container) return;
      if (!this.lottie) {
        this.lottie = (await import("lottie-web")).default;
      }

      if (this.delay) {
        await new Promise((res) => setTimeout(res, this.delay));
      }

      this.animation = this.lottie.loadAnimation({
        container: this.$refs.container,
        renderer: this.renderer,
        loop: this.loop,
        autoplay: this.autoplay,
        name: this.name,
        ...(this.animationData
          ? { animationData: this.animationData }
          : { path: this.path }),
      });

      if (this.callBackFun) {
        this.$emit("animControl", this.animation);
      }
    },

    initObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isVisible) {
              this.isVisible = true;
              this.loadLottie();
              this.observer.disconnect(); // load once
            }
          });
        },
        {
          threshold: 0.2,
        },
      );

      this.observer.observe(this.$refs.container);
    },

    destroyAnimation() {
      if (this.animation) {
        this.animation.destroy();
        this.animation = null;
      }
    },

    reloadAnimation() {
      if (!this.isVisible && this.lazy) return;

      this.destroyAnimation();
      this.$nextTick(() => this.loadLottie());
    },

    // Controls (optional use)
    play() {
      this.animation?.play();
    },
    pause() {
      this.animation?.pause();
    },
    stop() {
      this.animation?.stop();
    },
  },
};
</script>
